import { BidderSeat } from '@pubstack/common/src/bidder-seat'
import { BidderCatalog } from '@pubstack/common/src/bidderCatalog'
import { MEDIA_TYPES, MediaType } from '@pubstack/common/src/media-types'
import { Chip } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { FunctionComponent, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { NoResultsTableCell } from '~/components/_styled/table'
import Button from '~/components/Button'
import { BaseInputElement } from '~/components/Input'
import { Popover, PopoverContentWrapper, PopoverLine } from '~/components/Popover'
import Table, { handleTableSort, onColumnSort, TableColumns } from '~/components/table/Table'
import TableRow from '~/components/table/TableRow'
import { BidderRuleSeatRow } from './BidderRuleSeatRow'
import { BlockedSectionUpsell } from './BlockedSectionUpsell'
import {
  BidderRuleFormSection,
  ScopeCapabilities,
  SectionAvailability,
  SelectedBidderSeats,
  formSectionOfBidderSeat,
  isSeatEnabled,
  mediaTypeState,
  sectionAvailability,
  supportedMediaTypesOf,
} from './bidderRuleSections'

// Finer than `BidderRuleFormSection`: the two blocked/available sections the rest of the rule reasons
// about are still `client_side`/`server_side`, but a publisher filtering the seat list wants to find a
// deprecated integration or a Spark seat without reading a type chip on every row to spot one.
const SEAT_TYPE_FILTERS = ['Client-side', 'Server-side', 'Spark for Pubs', 'Server-side One (legacy)'] as const
type SeatTypeFilter = (typeof SEAT_TYPE_FILTERS)[number]
// `enabled` sits next to `all` rather than among the type chips: it is a state, not an integration, so it
// filters on top of whichever seats are already active in the rule instead of narrowing by where they bid
// from.
type TypeFilter = 'all' | 'enabled' | SeatTypeFilter

/** Which of the two blocked/available sections governs a filter, for the upsell it shows when blocked. */
const FORM_SECTION_OF_TYPE_FILTER: Record<SeatTypeFilter, BidderRuleFormSection> = {
  'Client-side': 'client_side',
  'Server-side': 'server_side',
  'Spark for Pubs': 'server_side',
  'Server-side One (legacy)': 'server_side',
}

/**
 * The finer type a seat files under here.
 *
 * Every Spark for Pubs seat, not only an internal one. This form loads no internal seats, so asking for
 * one left the chip matching nothing at all.
 */
const seatTypeFilterOf = (seat: BidderSeat): SeatTypeFilter => {
  if (seat.type === 'adm') return seat.platform === 'client' ? 'Client-side' : 'Server-side One (legacy)'
  if (seat.type === 'spark_for_pubs') return 'Spark for Pubs'
  return 'Server-side'
}

// `displayName` and `enabled` do not live on `BidderSeat` itself — the first comes from a catalog
// lookup, the second from `selected` — but `handleTableSort`'s `attributeSort` reads a plain key off
// whatever it sorts, so both are flattened onto the row once per render rather than re-derived per
// comparison.
type SeatRow = BidderSeat & { displayName: string; enabled: boolean }

// Media types has no `attributeSort`: it draws icons, not a value a reader would sort a list by. The
// last column's `name` is filled in at render time with the bulk enable/disable control, but it keeps
// its own sort here since enabled/disabled is exactly the kind of column a reader does sort a list by.
const INITIAL_COLUMNS: TableColumns<SeatRow> = [
  // Ascending, not 'none': `handleTableSort` falls back to this column whenever no column is actively
  // sorted, applying a *descending* sort under an icon that still reads as neutral — starting the sort
  // state where the icon already shows it avoids a hidden reorder on first render.
  { name: 'Bidder', isSortable: true, order: 'ascending', attributeSort: 'name' },
  { name: 'Adapter', isSortable: true, order: 'none', attributeSort: 'displayName' },
  { name: 'Media types', isSortable: false },
  { name: '', isSortable: true, order: 'none', attributeSort: 'enabled' },
]

type Props = {
  seats: BidderSeat[]
  catalogBidders: BidderCatalog[]
  selected: SelectedBidderSeats
  capabilities: ScopeCapabilities
  onToggleSeat: (seat: BidderSeat, enabled: boolean) => void
  onToggleMediaType: (seat: BidderSeat, mediaType: MediaType) => void
  onToggleAllSeats: (seats: BidderSeat[], enabled: boolean) => void
  /**
   * Which chip the table opens on. `enabled` on an existing rule, `all` on a new one — see the note on
   * the state below for why the two differ.
   */
  initialTypeFilter: TypeFilter
}

// About 7-10 rows' worth, plus a rough allowance for the controls bar above it — reserved space for the
// blocked-section upsell, which has no rows of its own. Also the floor for a page that has not been
// scrolled yet, so switching a filter or typing a search term that briefly matches nothing does not
// collapse the card and then re-expand it a moment later. A short or empty table simply leaves this space
// blank below it — its own header un-sticks past its (short) content, same as any other short table, and
// there is nothing further down worth keeping it pinned for.
const RESULTS_MIN_HEIGHT_PX = 8 * 48 + 60
const RESULTS_MIN_HEIGHT = `max(${RESULTS_MIN_HEIGHT_PX}px, 100vh)`

/**
 * Every seat of the rule in one searchable, filterable table, replacing the client-side/server-side
 * section lists. Search and a type filter narrow a long list to the handful a publisher is actually
 * looking for, the way the catalog's own controls do over its cards — and everything filtered in stays
 * on screen at once rather than behind a page number, so the controls that narrow it stay sticky above
 * it instead.
 *
 * A section the scope cannot edit never reaches this table at all — filtered out up front, the same
 * exclusion the old per-section list drew as an upsell in place of its rows. Picking that section's own
 * filter chip still shows the upsell, so the account is told why rather than shown an empty table.
 */
export const BidderRuleSeatTable: FunctionComponent<Props> = ({ seats, catalogBidders, selected, capabilities, onToggleSeat, onToggleMediaType, onToggleAllSeats, initialTypeFilter }) => {
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, 300)
  // Defaults to the seats already active in the rule — the ones a publisher opening an existing rule is
  // most likely here to check or adjust — rather than the full catalog of everything it could hold.
  //
  // A new rule has none, so the caller opens it on `all` instead: `enabled` there is an empty table on a
  // page whose whole purpose is to pick from the catalog, which reads as "there are no seats" rather than
  // "you have not chosen any yet".
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(initialTypeFilter)
  const [columns, setColumns] = useState<TableColumns<SeatRow>>(INITIAL_COLUMNS)
  const [mediaTypeMenuOpen, setMediaTypeMenuOpen] = useState(false)

  // Measured rather than guessed: the controls bar's chips wrap to a second line below a certain
  // viewport width, and a hardcoded offset drifted from its real height, leaving a gap the sticky
  // table header did not cover — a row scrolling through that gap looked clipped rather than tucked
  // cleanly underneath it. Starts at 0 so the header docks flush for the one frame before layout runs,
  // rather than flashing at a guessed offset that may not match.
  const controlsRef = useRef<HTMLDivElement>(null)
  const [controlsHeight, setControlsHeight] = useState(0)
  useLayoutEffect(() => {
    const controls = controlsRef.current
    if (!controls) return
    // `getBoundingClientRect`, not the observed entry's `contentRect`: the latter is the content box and
    // drops the `py-3` padding that is actually part of the bar's footprint, which would reopen the same
    // gap. Rounded up rather than truncated, so a fractional height never leaves a sub-pixel seam either.
    const observer = new ResizeObserver(() => setControlsHeight(Math.ceil(controls.getBoundingClientRect().height)))
    observer.observe(controls)
    return () => observer.disconnect()
  }, [])

  const editableSeats = useMemo(() => seats.filter((seat) => sectionAvailability(formSectionOfBidderSeat(seat), capabilities) === 'active'), [seats, capabilities])

  const displayNameOf = useCallback((seat: BidderSeat) => catalogBidders.find((bidder) => bidder.id === seat.bidderCode)?.displayName ?? seat.bidderCode, [catalogBidders])

  // `enabled` has no section of its own to be blocked on — a blocked section's seats never reach
  // `editableSeats` in the first place, so there is nothing for this filter to gate.
  const blockedSection = typeFilter === 'all' || typeFilter === 'enabled' ? undefined : FORM_SECTION_OF_TYPE_FILTER[typeFilter]
  const blockedAvailability: SectionAvailability | undefined = blockedSection && sectionAvailability(blockedSection, capabilities)
  // Legacy server-side One seats ride One: without it, the same offer as the client-side filter.
  const showOneOffer = typeFilter === 'Server-side One (legacy)' && !capabilities.hasOne
  // The Spark for Pubs chip stays empty on a scope with neither Spark product: say how to get one.
  const showSparkOffer = typeFilter === 'Spark for Pubs' && !capabilities.isSparkForPubAvailable && !capabilities.isSparkDemandAvailable

  const filteredSeats = useMemo(() => {
    const bySearch = editableSeats.filter((seat) => {
      const term = debouncedSearch.trim().toLowerCase()
      return !term || seat.name.toLowerCase().includes(term) || displayNameOf(seat).toLowerCase().includes(term)
    })
    if (typeFilter === 'all') return bySearch
    if (typeFilter === 'enabled') return bySearch.filter((seat) => isSeatEnabled(seat, selected))
    return bySearch.filter((seat) => seatTypeFilterOf(seat) === typeFilter)
  }, [editableSeats, displayNameOf, debouncedSearch, typeFilter, selected])

  const rows: SeatRow[] = useMemo(() => filteredSeats.map((seat) => ({ ...seat, displayName: displayNameOf(seat), enabled: isSeatEnabled(seat, selected) })), [filteredSeats, displayNameOf, selected])
  const sortedRows = useMemo(() => handleTableSort(columns, rows), [columns, rows])

  const enabledCount = rows.filter((row) => row.enabled).length
  const allEnabled = rows.length > 0 && enabledCount === rows.length
  // A search term or a type chip other than "All" narrows the table to a subset — "all" would overstate
  // what the bulk action actually reaches, so the label says "selection" instead once either is active.
  const isFiltered = debouncedSearch.trim() !== '' || typeFilter !== 'all'

  const mediaTypeAllEnabled = (mediaType: MediaType) => {
    const compatible = rows.filter((row) => supportedMediaTypesOf(row, catalogBidders).includes(mediaType))
    return compatible.length > 0 && compatible.every((row) => mediaTypeState(row, mediaType, selected, catalogBidders) === 'enabled')
  }

  // Flips only the rows that do not already match the target state, rather than toggling every
  // compatible row unconditionally — a row already at the target would otherwise get toggled away from
  // it, since `onToggleMediaType` flips relative to a seat's own current state, not sets it outright.
  const toggleMediaTypeForRows = (mediaType: MediaType) => {
    const enable = !mediaTypeAllEnabled(mediaType)
    rows
      .filter((row) => supportedMediaTypesOf(row, catalogBidders).includes(mediaType))
      .forEach((row) => {
        if ((mediaTypeState(row, mediaType, selected, catalogBidders) === 'enabled') !== enable) onToggleMediaType(row, mediaType)
      })
  }

  const enableAllHeader = rows.length > 0 && (
    <button
      type="button"
      // Bold against a header row that is not: `HeaderItem` renders at weight 400, so the one control
      // among the column names needs the weight to read as something to press rather than as a label.
      className="cursor-pointer whitespace-nowrap text-sm font-bold text-king hover:underline"
      onClick={(event) => {
        // Without this, the click also bubbles to the header cell's own sort handler — the column
        // sorts by `enabled` too, so a bulk toggle would silently re-sort the table under it.
        event.stopPropagation()
        onToggleAllSeats(rows, !allEnabled)
      }}
      data-tracking-label="common-bidder-rules-seats-apply"
    >
      {allEnabled ? (isFiltered ? 'Disable selection' : 'Disable all') : isFiltered ? 'Enable selection' : 'Enable all'}
    </button>
  )

  const mediaTypesHeader = (
    <span className="flex items-center gap-2">
      Media types
      <Popover
        align="end"
        open={mediaTypeMenuOpen}
        setOpen={setMediaTypeMenuOpen}
        trigger={<Button variant="tertiary" size="s" iconName="more_vert" title="Bulk media type actions" trackingLabel="common-bidder-rules-seat-media-type-actions-view" />}
      >
        <PopoverContentWrapper>
          {MEDIA_TYPES.map((mediaType) => (
            <PopoverLine
              key={mediaType}
              onClick={() => {
                setMediaTypeMenuOpen(false)
                toggleMediaTypeForRows(mediaType)
              }}
              data-tracking-label={`common-bidder-rules-seat-media-type-${mediaType}-apply`}
            >
              {mediaTypeAllEnabled(mediaType) ? `Disable ${mediaType}` : `Enable ${mediaType}`}
            </PopoverLine>
          ))}
        </PopoverContentWrapper>
      </Popover>
    </span>
  )

  const renderedColumns: TableColumns<SeatRow> = [columns[0], columns[1], { ...columns[2], name: mediaTypesHeader }, { ...columns[3], name: enableAllHeader }]

  return (
    <div className="flex flex-col gap-3">
      {/* `bg-white` so the table's own rows do not show through once this is pinned above them, and
          `z-10` so it stays above the table's own sticky header rather than tying with it. */}
      <div ref={controlsRef} className="sticky top-0 z-10 flex flex-wrap items-center gap-4 bg-white py-3">
        <BaseInputElement type="text" iconLeft="search" label="Search" labelIsPlaceholder value={search} onChange={(event) => setSearch(event.target.value)} />
        <Chip.Group multiple={false} value={typeFilter} onChange={(value) => setTypeFilter(value as TypeFilter)}>
          <div className="flex flex-wrap gap-2">
            <Chip value="all" variant="outline" size="sm" data-tracking="common-bidder-rules-seat-type-all-apply">
              All
            </Chip>
            <Chip value="enabled" variant="outline" size="sm" data-tracking="common-bidder-rules-seat-type-enabled-apply">
              Enabled
            </Chip>
            {SEAT_TYPE_FILTERS.map((type) => (
              <Chip key={type} value={type} variant="outline" size="sm" data-tracking={`common-bidder-rules-seat-type-${type.toLowerCase().replace(/[^a-z]+/g, '-')}-apply`}>
                {type}
              </Chip>
            ))}
          </div>
        </Chip.Group>
      </div>

      <div style={{ minHeight: RESULTS_MIN_HEIGHT }}>
        {showSparkOffer ? (
          <BlockedSectionUpsell section="spark" hasSeats={false} />
        ) : showOneOffer ? (
          <BlockedSectionUpsell section="client_side" hasSeats={seats.some((seat) => seatTypeFilterOf(seat) === typeFilter && isSeatEnabled(seat, selected))} />
        ) : blockedAvailability === 'blocked' && blockedSection ? (
          // `seats`, not `editableSeats`: the latter already excludes every seat of a blocked section, so
          // checking it here would always read `hasSeats={false}` and tell the account it holds nothing
          // even when its selection has not lost a single seat.
          <BlockedSectionUpsell section={blockedSection} hasSeats={seats.some((seat) => seatTypeFilterOf(seat) === typeFilter && isSeatEnabled(seat, selected))} />
        ) : (
          <Table columns={renderedColumns} isLoading={false} headersTop={controlsHeight} onClickHeading={(column) => onColumnSort(renderedColumns, column, setColumns)}>
            {sortedRows.length > 0 ? (
              sortedRows.map((row) => (
                <BidderRuleSeatRow key={row.id} seat={row} catalogBidders={catalogBidders} selected={selected} onToggleSeat={onToggleSeat} onToggleMediaType={onToggleMediaType} />
              ))
            ) : (
              <TableRow>
                <NoResultsTableCell colspan={renderedColumns.length}>No bidder seat matches your search.</NoResultsTableCell>
              </TableRow>
            )}
          </Table>
        )}
      </div>
    </div>
  )
}
